import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip,
    IconButton,
    CircularProgress,
    Tabs,
    Tab,
    Divider,
    Alert
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    Settings as SettingsIcon,
    Assessment as QuizIcon,
    BarChart as StatsIcon,
    Publish as PublishIcon,
    Unpublished as UnpublishIcon,
    AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { StaticQuizDTO, getAdminQuizzes, deleteQuiz, saveStaticQuiz } from '../../services/quizService';
import QuizRunner from '../Quiz/QuizRunner';

import QuizEditor from './QuizEditor';
import QuizConfigurationModal from './QuizConfigurationModal';

interface AdminQuizDashboardProps {
    topicId: number;
}

import { useParams } from 'react-router-dom';

const AdminQuizDashboard: React.FC<AdminQuizDashboardProps> = ({ topicId }) => {
    const { courseId } = useParams<{ courseId: string }>();
    const [quizzes, setQuizzes] = useState<StaticQuizDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const [previewQuiz, setPreviewQuiz] = useState<StaticQuizDTO | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [quizToEdit, setQuizToEdit] = useState<StaticQuizDTO | undefined>(undefined);
    const [openAIDialogOnMount, setOpenAIDialogOnMount] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Config Modal State
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);

    useEffect(() => {
        loadQuizzes();
    }, [topicId]);

    const loadQuizzes = async () => {
        setLoading(true);
        try {
            const data = await getAdminQuizzes(topicId);
            setQuizzes(data);
        } catch (error) {
            console.error('Failed to load quizzes', error);
        } finally {
            setLoading(false);
        }
    };
    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this quiz?')) {
            await deleteQuiz(id);
            loadQuizzes();
        }
    };

    const handleCreateClick = () => {
        setQuizToEdit(undefined);
        setOpenAIDialogOnMount(false);
        setIsEditing(true);
    };

    const handleCreateWithAIClick = () => {
        setQuizToEdit(undefined);
        setOpenAIDialogOnMount(true);
        setIsEditing(true);
    };

    const handleEditClick = (quiz: StaticQuizDTO) => {
        setQuizToEdit(quiz);
        setIsEditing(true);
    };

    const handleSaveQuiz = async (quizData: Partial<StaticQuizDTO>) => {
        setSaveError(null);
        try {
            await saveStaticQuiz(topicId, {
                ...quizData,
                courseId: Number(courseId) || 0
            });
            setIsEditing(false);
            loadQuizzes();
        } catch (error: any) {
            const msg = error?.response?.data?.error ?? error?.response?.data?.message ?? error?.message;
            console.error("Failed to save quiz", error);
            console.error("Backend response:", error?.response?.status, error?.response?.data);
            setSaveError(msg || "Failed to save quiz");
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setQuizToEdit(undefined);
        setOpenAIDialogOnMount(false);
        setSaveError(null);
    };

    const handleTogglePublish = async (quiz: StaticQuizDTO) => {
        const newStatus = quiz.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
        try {
            await saveStaticQuiz(topicId, {
                ...quiz,
                status: newStatus
            });
            loadQuizzes();
        } catch (error) {
            console.error("Failed to update quiz status", error);
            alert("Failed to update quiz status");
        }
    };

    if (isEditing) {
        return (
            <QuizEditor
                topicId={topicId}
                existingQuiz={quizToEdit}
                onSave={handleSaveQuiz}
                onCancel={handleCancelEdit}
                openAIDialogOnMount={openAIDialogOnMount}
                saveError={saveError}
                onClearSaveError={() => setSaveError(null)}
            />
        );
    }

    if (previewQuiz) {
        return (
            <Box sx={{ height: '80vh' }}>
                <QuizRunner
                    quiz={previewQuiz}
                    mode="PREVIEW"
                    onComplete={() => alert('Preview Completed')}
                    onExit={() => setPreviewQuiz(null)}
                />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">Practice Quizzes</Typography>
                <Box display="flex" gap={2}>
                    <Button variant="outlined" color="secondary" startIcon={<AutoAwesomeIcon />} onClick={handleCreateWithAIClick}>
                        Create with AI
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateClick}>Create Quiz</Button>
                </Box>
            </Box>


            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab label="Manage Quizzes" icon={<QuizIcon />} iconPosition="start" />
                    <Tab label="Performance" icon={<StatsIcon />} iconPosition="start" />
                    <Tab label="Configuration" icon={<SettingsIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {tabValue === 0 && (
                <Grid container spacing={3}>
                    {loading && <CircularProgress />}
                    {!loading && quizzes.length === 0 && (
                        <Grid item xs={12}>
                            <Alert severity="info">No practice quizzes found. Create one to get started.</Alert>
                        </Grid>
                    )}

                    {quizzes.map((quiz) => (
                        <Grid item xs={12} md={6} key={quiz.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight="bold">{quiz.title}</Typography>
                                    <Box display="flex" gap={1} mt={1} mb={1}>
                                        <Chip label={`${quiz.questions.length} Qs`} size="small" />
                                        <Chip label={quiz.type} color="primary" variant="outlined" size="small" />
                                        <Chip label={quiz.status} size="small" color={quiz.status === 'PUBLISHED' ? 'success' : 'default'} />
                                    </Box>
                                    {quiz.stats && (
                                        <Typography variant="caption" color="text.secondary">
                                            Tried by {quiz.stats.uniqueUsers} users | Avg Score: {quiz.stats.avgScore}%
                                        </Typography>
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Button size="small" startIcon={<PlayIcon />} onClick={() => setPreviewQuiz(quiz)}>
                                        Try as Student
                                    </Button>
                                    <Button 
                                        size="small" 
                                        startIcon={quiz.status === 'PUBLISHED' ? <UnpublishIcon /> : <PublishIcon />}
                                        color={quiz.status === 'PUBLISHED' ? 'warning' : 'success'}
                                        onClick={() => handleTogglePublish(quiz)}
                                    >
                                        {quiz.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
                                    </Button>
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditClick(quiz)}>Edit</Button>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(quiz.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {tabValue === 1 && (
                <Box py={8} textAlign="center">
                    <Typography color="text.secondary">Performance metrics coming soon...</Typography>
                </Box>
            )}

            {tabValue === 2 && (
                <Box py={4}>
                    <Typography variant="h6" gutterBottom>Quiz Configuration</Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Configure default settings for practice quizzes in this topic.
                        These settings override subject and course levels.
                    </Typography>

                    <Button
                        variant="contained"
                        startIcon={<SettingsIcon />}
                        onClick={() => setIsConfigModalOpen(true)}
                    >
                        Manage Configuration
                    </Button>

                    <QuizConfigurationModal
                        open={isConfigModalOpen}
                        onClose={() => setIsConfigModalOpen(false)}
                        level="TOPIC"
                        entityId={topicId}
                    />
                </Box>
            )}
        </Box>
    );
};

export default AdminQuizDashboard;
